import { useState } from "react";
import { useEditorStore, type StoreLayer } from "@/store";
import { LAYER_TYPE_COLORS, LAYER_TYPE_ICONS, type LayerType } from "@/styles/theme";
import { generateLayerId } from "@/lib/styleUtils";
import { useSourceLayers } from "@/hooks/useSourceLayers";

const LAYER_TYPES: { type: LayerType; label: string; description: string }[] = [
  { type: "background", label: "Background", description: "Solid color or pattern fill" },
  { type: "fill", label: "Fill", description: "Polygon fills" },
  { type: "line", label: "Line", description: "Line strokes" },
  { type: "symbol", label: "Symbol", description: "Text labels and icons" },
  { type: "circle", label: "Circle", description: "Point circles" },
  { type: "heatmap", label: "Heatmap", description: "Density visualization" },
  { type: "fill-extrusion", label: "Fill Extrusion", description: "3D extruded polygons" },
  { type: "raster", label: "Raster", description: "Raster tile imagery" },
  { type: "hillshade", label: "Hillshade", description: "Terrain shading" },
];

interface AddLayerDialogProps {
  onClose: () => void;
}

export function AddLayerDialog({ onClose }: AddLayerDialogProps) {
  const [selectedType, setSelectedType] = useState<LayerType | null>(null);
  const [layerId, setLayerId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [sourceLayer, setSourceLayer] = useState("");

  const { sourceLayers } = useSourceLayers(sourceId || undefined);
  const addLayer = useEditorStore((s) => s.addLayer);
  const setSelectedLayerId = useEditorStore((s) => s.setSelectedLayerId);
  const sources = useEditorStore((s) => s.style.sources ?? {}) as Record<string, Record<string, unknown>>;
  const sourceIds = Object.keys(sources);

  const needsSource = selectedType && selectedType !== "background";

  const handleAdd = (position: "top" | "bottom") => {
    if (!selectedType) return;
    const id = layerId || generateLayerId(selectedType);

    const layer: StoreLayer = {
      id,
      type: selectedType,
      paint: {},
    };

    if (needsSource && sourceId) {
      layer.source = sourceId;
      if (sourceLayer) {
        layer["source-layer"] = sourceLayer;
      }
    }

    addLayer(layer, position);
    setSelectedLayerId(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="w-96 rounded-radius-lg border border-border-subtle bg-surface-raised px-6 py-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-sm font-semibold text-text-primary">Add Layer</h2>

        {/* Type selection */}
        <div className="mb-4 grid grid-cols-3 gap-1.5">
          {LAYER_TYPES.map((lt) => (
            <button
              key={lt.type}
              type="button"
              onClick={() => setSelectedType(lt.type)}
              className={`flex flex-col items-center gap-1 rounded-radius-sm p-2 text-center transition-colors ${
                selectedType === lt.type
                  ? "bg-accent-muted ring-1 ring-accent"
                  : "bg-surface-sunken hover:bg-surface-base"
              }`}
            >
              <span
                className="flex h-6 w-6 items-center justify-center rounded-radius-sm text-xs text-white"
                style={{ backgroundColor: LAYER_TYPE_COLORS[lt.type] }}
              >
                {LAYER_TYPE_ICONS[lt.type]}
              </span>
              <span className="text-[10px] font-medium text-text-primary">{lt.label}</span>
            </button>
          ))}
        </div>

        {/* Config fields */}
        {selectedType && (
          <div className="space-y-2">
            <div>
              <label className="mb-1 block text-[10px] font-medium text-text-secondary">Layer ID</label>
              <input
                type="text"
                value={layerId}
                onChange={(e) => setLayerId(e.target.value)}
                placeholder={generateLayerId(selectedType)}
                className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
              />
            </div>

            {needsSource && (
              <>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-text-secondary">Source</label>
                  <select
                    value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}
                    className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary focus:border-accent focus:outline-none"
                  >
                    <option value="">Select source...</option>
                    {sourceIds.map((id) => (
                      <option key={id} value={id}>{id}</option>
                    ))}
                  </select>
                </div>

                {sourceId && sources[sourceId]?.type === "vector" && (
                  <div>
                    <label className="mb-1 block text-[10px] font-medium text-text-secondary">Source Layer</label>
                    {sourceLayers.length > 0 ? (
                      <select
                        value={sourceLayer}
                        onChange={(e) => setSourceLayer(e.target.value)}
                        className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary focus:border-accent focus:outline-none"
                      >
                        <option value="">Select source layer...</option>
                        {sourceLayers.map((sl) => (
                          <option key={sl.id} value={sl.id}>
                            {sl.description ? `${sl.id} — ${sl.description}` : sl.id}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={sourceLayer}
                        onChange={(e) => setSourceLayer(e.target.value)}
                        placeholder="e.g. countries"
                        className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="rounded-radius-sm px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleAdd("bottom")}
              disabled={!selectedType}
              className="rounded-radius-sm border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent disabled:opacity-40 hover:bg-accent hover:text-accent-text transition-colors"
              title="Renders behind all other layers"
            >
              Add to bottom
            </button>
            <button
              type="button"
              onClick={() => handleAdd("top")}
              disabled={!selectedType}
              className="rounded-radius-sm bg-accent px-3 py-1.5 text-xs font-medium text-accent-text disabled:opacity-40 hover:bg-accent-hover transition-colors"
              title="Renders on top of all other layers"
            >
              Add to top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
